import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChevronDown,
  faDollarSign,
  faMagnifyingGlass,
  faRulerCombined,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import ListingCard from "../components/listingCard.component";
import Pagination from "../components/pagination.component";
import BouncingDotsLoader from "./loader.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { formatStr } from "../functions/listings";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function CommercialListings() {
  const { state } = useLocation();
  const seoConfig = pageSEO.listings();
  const maxListings = 15;
  const [listings, setListings] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("All");
  const [error, setError] = useState("");
  const commercialRef = useRef(null);

  //dummy listings
  const demoListings = data.value.filter(
    listing =>
      (listing.PropertyType || "").toLowerCase().includes("commercial")
  );

  const [searchParams, setSearchParams] = useState({
    type: "",
    sqft: "",
    minPrice: "",
    maxPrice: "",
  });

  /* API REQUEST*/
  async function fetchData(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };
    const response =
      constants.ENVIRONMENT === "production"
        ? await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw Error("API ERROR");
    return response.json();
  }

  /* FORMAT IMAGES */
  function formatImages(media) {
    return media.reduce((acc, item) => {
      if (!acc[item.ResourceRecordKey])
        acc[item.ResourceRecordKey] = [];
      acc[item.ResourceRecordKey].push(item);
      return acc;
    }, {});
  }

  /* LOAD MEDIA */
  async function loadImages(data) {
    if (!data.length) return;

    const ids = data
      .map(item => `'${item.ListingKey}'`)
      .join(",");

    let url;

    if (constants.ENVIRONMENT === "production") {
      url =
        `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(${ids})(S)and(S)ImageSizeDescription(S)eq(S)'Thumbnail'(S)and(S)PreferredPhotoYN(S)eq(S)true`;
    } else {
      url =
        `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (${ids}) and ImageSizeDescription eq 'Thumbnail' and PreferredPhotoYN eq true`;
    }

    const result = await fetchData(url);

    setImages(formatImages(result.value));
  }

  /* LOAD LISTINGS */
  async function loadListings() {
    try {
      setLoading(true);

      let filterQuery =
        "contains(PropertyType, 'Commercial')";

      if (filter === "Office") {
        filterQuery +=
          ` and ListOfficeKey eq '${constants.OFFICE_ID}'`;
      }

      Object.values(searchParams)
        .filter(Boolean)
        .forEach(item => {
          filterQuery +=
            ` and ${item}`;
        });

      // Format only for production
      const finalFilter =
        constants.ENVIRONMENT === "production"
          ? formatStr(filterQuery)
          : filterQuery;

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=${filterQuery}(A)$orderby=OriginalEntryTimestamp(S)desc&$top=500`
        :
        `https://query.ampre.ca/odata/Property?$filter=${filterQuery}&$orderby=OriginalEntryTimestamp desc&$top=500`;

      const data =
        await fetchData(url);

      console.log("data", data);

      if (data.value) {
        setListings(data.value);

        await loadImages(
          data.value.slice(
            0,
            maxListings
          )
        );
        setError("");
      }
    }
    catch (error) {
      console.log(error);
      setListings([]);
      setImages({});

      // setError(
      //   "Unable to load listings."
      // );
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    console.log("FILTER", filter)
    loadListings()
  }, [filter, searchParams]);

  useEffect(() => {
    async function updatePageImages() {
      const data =
        listings.slice(
          (page - 1) * maxListings,
          page * maxListings
        );

      await loadImages(data);
    }
    if (listings.length) {
      updatePageImages();
    }
  }, [page]);

  /* SEARCH UPDATE */
  function updateSearch(key, value) {
    setSearchParams(prev => ({
      ...prev,
      [key]: value
    }));
  }

  function handleSearch() {
    setPage(1);
  }

  function clearSearch() {
    setSearchParams({
      type: "",
      sqft: "",
      minPrice: "",
      maxPrice: "",
    });

    setFilter("All");
    setPage(1);
  }

  /* PAGINATION */
  // const totalPages =
  //   Math.ceil(
  //     listings.length / maxListings
  //   );

  // const visibleListings =
  //   listings.slice(
  //     (page - 1) * maxListings,
  //     page * maxListings
  //   );

  const totalPages =
    Math.ceil(
      demoListings.length / maxListings
    );

  const visibleListings =
    demoListings.slice(
      (page - 1) * maxListings,
      page * maxListings
    );

  const handlePageChange = (newPage) => {
    setPage(newPage);

    if (commercialRef.current) {
      commercialRef.current.scrollIntoView({
        block: "start",
      });
    }
  };

  return (
    <>
      <SEO {...seoConfig} />

      <section className="max-w-[1600px] mx-auto px-6 pt-40 pb-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Heading */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 border-b border-slate-200 pb-10"
          >
            <div>
              <p
                className="uppercase tracking-[0.5em] font-semibold text-cottage-accent"
              >
                Commercial Property Search
              </p>

              <h1 className="mt-6 text-5xl lg:text-6xl font-light font-serif tracking-tight text-harbour-navy leading-none">
                Commercial Space
              </h1>
            </div>
          </motion.div>

          {/* Search Fields */}
          <motion.div
            variants={fadeUp}
            className=" mt-12 rounded-lg border border-slate-200 bg-white /95 backdrop-blur-xl p-8 lg:p-10 shadow-xl"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">

              {/* Property Type */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <select
                  value={searchParams.type}
                  onChange={(e) => updateSearch("type", e.target.value)}
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                >
                  <option value="">Property Type</option>

                  <option value="contains(PropertySubType, 'Office')">
                    Office
                  </option>

                  <option value="contains(PropertySubType, 'Retail')">
                    Retail
                  </option>

                  <option value="contains(PropertySubType, 'Investment')">
                    Investment
                  </option>

                  <option value="contains(PropertySubType, 'Industrial')">
                    Industrial
                  </option>
                </select>

                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400"
                />
              </div>

              {/* Square Footage */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faRulerCombined}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <select
                  value={searchParams.sqft}
                  onChange={(e) => updateSearch("sqft", e.target.value)}
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                >
                  <option value="">Square Footage</option>

                  <option value="BuildingAreaTotal lt 500">
                    Under 500 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 500 and BuildingAreaTotal le 1000">
                    500 - 1000 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 1000 and BuildingAreaTotal le 1500">
                    1000 - 1500 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 1500 and BuildingAreaTotal le 2000">
                    1500 - 2000 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 2000">
                    2000+ Sq Ft
                  </option>
                </select>

                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400"
                />
              </div>

              {/* Minimum Price */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <input
                  value={searchParams.minPrice.replace(/\D/g, "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    updateSearch(
                      "minPrice",
                      value ? `ListPrice ge ${value}` : ""
                    );
                  }}
                  placeholder="Minimum Price"
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                />
              </div>

              {/* Maximum Price */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <input
                  value={searchParams.maxPrice.replace(/\D/g, "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    updateSearch(
                      "maxPrice",
                      value ? `ListPrice le ${value}` : ""
                    );
                  }}
                  placeholder="Maximum Price"
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                />
              </div>

            </div>
            {/* Actions */}
            <div
              variants={fadeUp}
              className="mt-10 flex flex-col justify-end gap-3 sm:flex-row"
            >
              <button
                onClick={handleSearch}
                className="btn-cottage"
              >
                Search Properties
              </button>

              <button
                onClick={clearSearch}
                className="btn-cottage-outline-light"
              >
                Clear
              </button>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          ref={commercialRef}
        >
          <motion.div
            variants={fadeUp}
            className="mt-24 flex flex-col gap-6 border-b border-slate-200 pb-8 sm:flex-row sm:items-end sm:justify-between mx-5 lg:mx-24">
            <div>
              <p className="mb-3 uppercase tracking-[0.4em] text-harbour-navy">
                Property Collection
              </p>

              <h1 className="text-4xl font-light uppercase tracking-wider text-harbour-navy libre">
                Recent Listings
              </h1>
            </div>

            <div className="w-full sm:w-52">
              <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-slate-500">
                Show Listings
              </label>

              <div className="relative">
                <select
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm uppercase tracking-wider text-slate-700 shadow-sm outline-none transition focus:border-harbour-navy"
                  value={filter}
                  onChange={(e) => {
                    setPage(1);
                    setFilter(e.target.value);
                    // setSearching(4);
                    // runSearch(e.target.value);
                  }}
                >
                  <option value="All">
                    All Listings
                  </option>

                  <option value="Office">
                    Office Listings
                  </option>

                  <option value="Agent">
                    Agent Listings
                  </option>
                </select>

                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-harbour-navy">
                  ▼
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {loading && (
          <div className="text-center py-20">
            <div className="mt-32 min-h-[20vh]">
              <BouncingDotsLoader />
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-20">

            <div className="inline-block rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-700">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mr-2"
              />
              {error}
            </div>
          </div>
        )}

        {!loading && !error && visibleListings.length > 0 && (
          < div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 py-10 px-4 lg:px-24">
            {visibleListings.map((property) => (
              <ListingCard
                key={property.ListingKey}
                property={property}
                // image={images[property.ListingKey]?.[0]}
                image={property.MainImgUrl} // for local without api fetch show images
                type="commercial"
              />
            ))}
          </div>
        )}

        {!loading && !error && visibleListings.length === 0 && (
          <div className="text-center py-20 tracking-wider text-xl">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="mr-2 text-harbour-navy"
            />
            No properties to show
          </div>
        )}

        <Pagination
          page={page}
          setPage={handlePageChange}
          totalPages={totalPages}
          totalItems={listings.length}
          pageSize={maxListings}
        />
      </section >
    </>
  );
}